import React, { useCallback, useEffect, useState } from 'react'
import {
  getBillableFlowDetails,
  type BillableFlowDetail,
  type BillableFlowDetailsParams
} from '~/services/usageDashboardService'
import { cm } from '~/utils/utils'
import { Modal } from '../modals/CurieModal'
import { Pagination } from '../common/Pagination'
import { Typography } from '../common/Typography'
import { DialogHeader } from './DialogHeader'

const TYPE_BADGE_STYLES: Record<string, string> = {
  HTTP: 'bg-blue-100 text-blue-700',
  EVENT: 'bg-green-100 text-green-700',
  SOAP: 'bg-orange-100 text-orange-700'
}

function TypeBadge({ type }: { type: string }) {
  const style = TYPE_BADGE_STYLES[type] ?? 'bg-gray-100 text-gray-600'
  return (
    <span
      className={cm(
        'inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium select-none',
        style
      )}
    >
      {type}
    </span>
  )
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return ''
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  } catch {
    return iso
  }
}

function extractRepoShortName(repoName: string): string {
  if (!repoName) return ''
  const parts = repoName.split('/')
  return parts[parts.length - 1] || repoName
}

const PAGE_SIZE = 10
const API_BATCH_SIZE = 500

interface BillableFlowsPanelProps {
  isOpen: boolean
  onClose: () => void
  billingAccountId: number
  params: BillableFlowDetailsParams
  /** Hide the credits column and total (for migration/billable-flow plans) */
  hideCredits?: boolean
  /** Super admins can see credits; non-super-admins always have credits hidden */
  isSuperAdmin?: boolean
}

export const BillableFlowsPanel: React.FC<BillableFlowsPanelProps> = ({
  isOpen,
  onClose,
  billingAccountId,
  params,
  hideCredits = false,
  isSuperAdmin = false
}) => {
  const [allFlows, setAllFlows] = useState<BillableFlowDetail[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [fetchError, setFetchError] = useState<string | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalTasks, setTotalTasks] = useState(0)
  const [totalCredits, setTotalCredits] = useState(0)

  const totalFlows = allFlows.length
  const totalPages = Math.max(1, Math.ceil(totalFlows / PAGE_SIZE))
  const flows = allFlows.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE)

  const fetchAllFlows = useCallback(async () => {
    setIsLoading(true)
    setFetchError(null)
    try {
      const fetchedFlows: BillableFlowDetail[] = []
      let page = 1
      while (true) {
        const data = await getBillableFlowDetails(billingAccountId, {
          ...params,
          page,
          pageSize: API_BATCH_SIZE
        })
        setTotalTasks(data.total_tasks ?? 0)
        setTotalCredits(data.total_credits ?? 0)
        const batch = data.flows ?? []
        if (batch.length === 0) break
        fetchedFlows.push(...batch)

        const totalFromApi = data.total
        const reachedReportedTotal =
          typeof totalFromApi === 'number' &&
          totalFromApi > 0 &&
          fetchedFlows.length >= totalFromApi
        if (reachedReportedTotal) break
        // If `total` is missing/wrong upstream, still drain full pages by batch size.
        if (batch.length < API_BATCH_SIZE) break
        page++
      }
      setAllFlows(fetchedFlows)
    } catch {
      setAllFlows([])
      setTotalTasks(0)
      setTotalCredits(0)
      setFetchError('Failed to load billable flows. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }, [billingAccountId, params])

  useEffect(() => {
    if (isOpen) {
      setCurrentPage(1)
      fetchAllFlows()
    }
  }, [isOpen, fetchAllFlows])

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const shouldHideCredits = hideCredits || !isSuperAdmin
  const [showDownloadMenu, setShowDownloadMenu] = useState(false)

  const downloadFlows = (format: 'csv' | 'json') => {
    setShowDownloadMenu(false)
    const data = allFlows.map(f => ({
      'Flow Name': f.flow_name || 'Unknown Flow',
      Workspace: f.workspace_name || 'No Workspace',
      Repo: f.repo_name || 'No Repository',
      Type: f.flow_type || 'No Type',
      Date: f.date || 'No Date',
      'Task ID': f.kept_task_id ?? 'No Task'
    }))
    if (data.length === 0) return

    const filename = `billable-flows-${new Date().toISOString().slice(0, 10)}`
    let blob: Blob
    let ext: string

    if (format === 'json') {
      blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
      ext = 'json'
    } else {
      const headers = Object.keys(data[0])
      const csvRows = [
        headers.join(','),
        ...data.map(row =>
          headers
            .map(h => {
              const val = String((row as Record<string, unknown>)[h] ?? '')
              return val.includes(',') || val.includes('"') ? `"${val.replace(/"/g, '""')}"` : val
            })
            .join(',')
        )
      ]
      blob = new Blob([csvRows.join('\n')], { type: 'text/csv' })
      ext = 'csv'
    }

    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${filename}.${ext}`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      className='w-full max-w-[800px]'
      overlayClassName='px-[80px]'
      hideCloseButton
      rawContent
    >
      <div className='flex flex-col bg-[#EFF0F3] max-h-[100vh]'>
        <DialogHeader title='Billable Flows' onClose={onClose} className='sticky top-0 z-10'>
          {flows.length > 0 && (
            <div className='relative'>
              <button
                className='h-8 flex items-center gap-2 px-3 rounded-xl bg-[#65558F] text-white text-xs font-medium transition-colors hover:bg-[#7A6BA3] active:bg-[#584B7A] select-none'
                onClick={() => setShowDownloadMenu(!showDownloadMenu)}
                onBlur={() => setTimeout(() => setShowDownloadMenu(false), 150)}
              >
                <svg
                  width='14'
                  height='14'
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                >
                  <path d='M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4' />
                  <polyline points='7 10 12 15 17 10' />
                  <line x1='12' y1='15' x2='12' y2='3' />
                </svg>
                Download
                <svg width='8' height='5' viewBox='0 0 10 6' fill='none'>
                  <path
                    d='M5 4.293L1.354.646.646 1.354 5 5.707l4.354-4.353L8.646.646 5 4.293z'
                    fill='white'
                  />
                </svg>
              </button>
              {showDownloadMenu && (
                <div className='absolute right-0 top-full mt-1 bg-white rounded-2xl shadow-lg border border-gray-200 py-1 z-20 min-w-[160px]'>
                  <button
                    className='w-full px-4 py-2.5 text-left text-sm text-[rgba(0,0,0,0.88)] hover:bg-[#FAFAFA] border-b border-gray-100 rounded-t-2xl'
                    onMouseDown={() => downloadFlows('csv')}
                  >
                    CSV (.csv)
                  </button>
                  <button
                    className='w-full px-4 py-2.5 text-left text-sm text-[rgba(0,0,0,0.88)] hover:bg-[#FAFAFA] rounded-b-2xl'
                    onMouseDown={() => downloadFlows('json')}
                  >
                    JSON (.json)
                  </button>
                </div>
              )}
            </div>
          )}
        </DialogHeader>

        <div className='px-8 py-6 overflow-y-auto flex-1'>
          {/* Primary summary row; Total Credits below when shown (super-admin extra on BF plans) */}
          <div className='flex flex-col gap-4 mb-6'>
            <div className='flex gap-4'>
              <div className='flex-1 bg-white rounded-2xl px-6 py-4 border border-[#EAEAEA]'>
                <Typography
                  fontFamily='geist'
                  fontSize='bodySm'
                  fontWeight='medium'
                  className='text-[rgba(0,0,0,0.45)] mb-1 select-none'
                >
                  Billable Flows
                </Typography>
                <Typography
                  fontFamily='geist'
                  fontSize='titleLg'
                  fontWeight='bold'
                  className='text-[#65558F]'
                >
                  {totalFlows.toLocaleString()}
                </Typography>
              </div>
              {isSuperAdmin && (
                <div className='flex-1 bg-white rounded-2xl px-6 py-4 border border-[#EAEAEA]'>
                  <Typography
                    fontFamily='geist'
                    fontSize='bodySm'
                    fontWeight='medium'
                    className='text-[rgba(0,0,0,0.45)] mb-1 select-none'
                  >
                    Tasks
                  </Typography>
                  <Typography
                    fontFamily='geist'
                    fontSize='titleLg'
                    fontWeight='bold'
                    className='text-[#65558F]'
                  >
                    {totalTasks}
                  </Typography>
                </div>
              )}
            </div>
            {!shouldHideCredits && (
              <div className='flex gap-4'>
                <div className='flex-1 bg-white rounded-2xl px-6 py-4 border border-[#EAEAEA]'>
                  <Typography
                    fontFamily='geist'
                    fontSize='bodySm'
                    fontWeight='medium'
                    className='text-[rgba(0,0,0,0.45)] mb-1 select-none'
                  >
                    Total Credits
                  </Typography>
                  <Typography
                    fontFamily='geist'
                    fontSize='titleLg'
                    fontWeight='bold'
                    className='text-[#65558F]'
                  >
                    {totalCredits.toLocaleString()}
                  </Typography>
                </div>
              </div>
            )}
          </div>

          {isLoading && flows.length === 0 && (
            <div className='text-center py-8'>
              <Typography
                fontFamily='geist'
                fontSize='bodySm'
                fontWeight='normal'
                className='text-[rgba(0,0,0,0.45)] select-none'
              >
                Loading flows...
              </Typography>
            </div>
          )}

          {!isLoading && fetchError && (
            <div className='text-center py-8'>
              <Typography
                fontFamily='geist'
                fontSize='bodySm'
                fontWeight='normal'
                className='text-red-500 select-none'
              >
                {fetchError}
              </Typography>
            </div>
          )}

          {!isLoading && !fetchError && flows.length === 0 && (
            <div className='text-center py-8'>
              <Typography
                fontFamily='geist'
                fontSize='bodySm'
                fontWeight='normal'
                className='text-[rgba(0,0,0,0.45)] select-none'
              >
                No billable flows in this period
              </Typography>
            </div>
          )}

          {!fetchError && flows.length > 0 && (
            <>
              <div
                className={cm(
                  'bg-white rounded-2xl border border-[#EAEAEA] overflow-hidden transition-opacity duration-150',
                  isLoading ? 'opacity-50 pointer-events-none' : 'opacity-100'
                )}
              >
                <table className='w-full text-left text-sm'>
                  <thead>
                    <tr className='border-b border-[#EAEAEA] bg-[#FAFAFA]'>
                      <th className='px-4 py-3 font-medium text-[rgba(0,0,0,0.45)] text-xs select-none'>
                        Flow Name
                      </th>
                      <th className='px-4 py-3 font-medium text-[rgba(0,0,0,0.45)] text-xs select-none'>
                        Repo
                      </th>
                      <th className='px-4 py-3 font-medium text-[rgba(0,0,0,0.45)] text-xs select-none'>
                        Type
                      </th>
                      <th className='px-4 py-3 font-medium text-[rgba(0,0,0,0.45)] text-xs select-none'>
                        Date
                      </th>
                      <th className='px-4 py-3 font-medium text-[rgba(0,0,0,0.45)] text-xs select-none'>
                        {isSuperAdmin ? 'Task IDs' : 'Task ID'}
                      </th>
                      {!shouldHideCredits && (
                        <th className='px-4 py-3 font-medium text-[rgba(0,0,0,0.45)] text-xs text-right select-none'>
                          Credits
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {flows.map((flow, idx) => {
                      const repoShort = extractRepoShortName(flow.repo_name ?? '')
                      return (
                        <tr
                          key={`${flow.flow_name}-${flow.repo_name}-${idx}`}
                          className='border-b border-[#F5F5F5] last:border-b-0 hover:bg-[#FAFAFA] transition-colors'
                        >
                          <td className='px-4 py-3 max-w-[280px] overflow-hidden'>
                            <div
                              className='text-[rgba(0,0,0,0.88)] truncate'
                              title={flow.flow_name || undefined}
                            >
                              {flow.flow_name || (
                                <span className='italic text-[rgba(0,0,0,0.35)]'>Unknown Flow</span>
                              )}
                            </div>
                            {flow.workspace_name ? (
                              <div
                                className='text-[rgba(0,0,0,0.35)] text-xs truncate mt-0.5'
                                title={flow.workspace_name}
                              >
                                {flow.workspace_name}
                              </div>
                            ) : (
                              <div className='text-xs mt-0.5'>
                                <span className='italic text-[rgba(0,0,0,0.25)]'>No Workspace</span>
                              </div>
                            )}
                          </td>
                          <td
                            className='px-4 py-3 text-sm truncate max-w-[180px] overflow-hidden'
                            title={flow.repo_name ?? undefined}
                          >
                            {repoShort ? (
                              <span className='text-[rgba(0,0,0,0.65)]'>{repoShort}</span>
                            ) : (
                              <span className='italic text-[rgba(0,0,0,0.35)]'>No Repository</span>
                            )}
                          </td>
                          <td className='px-4 py-3'>
                            {flow.flow_type ? (
                              <TypeBadge type={flow.flow_type} />
                            ) : (
                              <span className='italic text-[rgba(0,0,0,0.35)]'>No Type</span>
                            )}
                          </td>
                          <td className='px-4 py-3 whitespace-nowrap'>
                            {formatDate(flow.date) ? (
                              <span className='text-[rgba(0,0,0,0.65)]'>
                                {formatDate(flow.date)}
                              </span>
                            ) : (
                              <span className='italic text-[rgba(0,0,0,0.35)]'>No Date</span>
                            )}
                          </td>
                          <td className='px-4 py-3'>
                            {isSuperAdmin ? (
                              (flow.task_ids ?? []).length > 0 ? (
                                <span className='text-[rgba(0,0,0,0.65)]'>
                                  {flow.task_ids!.join(', ')}
                                </span>
                              ) : (
                                <span className='italic text-[rgba(0,0,0,0.35)]'>No Task</span>
                              )
                            ) : flow.kept_task_id != null ? (
                              <span className='text-[rgba(0,0,0,0.65)]'>{flow.kept_task_id}</span>
                            ) : (
                              <span className='italic text-[rgba(0,0,0,0.35)]'>No Task</span>
                            )}
                          </td>
                          {!shouldHideCredits && (
                            <td className='px-4 py-3 text-right'>
                              {flow.credits_used != null ? (
                                <span className='text-[rgba(0,0,0,0.65)]'>
                                  {flow.credits_used.toLocaleString()}
                                </span>
                              ) : (
                                <span className='italic text-[rgba(0,0,0,0.35)]'>—</span>
                              )}
                            </td>
                          )}
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                  className='mt-4'
                />
              )}
            </>
          )}
        </div>
      </div>
    </Modal>
  )
}
